import { useEffect, useRef } from "react";

const STORAGE_KEY = "sentinelops.audio.muted";

/**
 * Triggers a short WebAudio "beep" when a new critical or high alert arrives.
 * Caller passes the live alerts list; we keep a ref of last-seen ids to detect deltas.
 */
export const useAudioCue = (alerts, { enabled = true } = {}) => {
  const seenRef = useRef(new Set());
  const firstRunRef = useRef(true);
  const ctxRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (!alerts || alerts.length === 0) return;

    // Skip beeps on initial hydration; record their ids and bail.
    if (firstRunRef.current) {
      for (const a of alerts) seenRef.current.add(a.id);
      firstRunRef.current = false;
      return;
    }

    const muted = localStorage.getItem(STORAGE_KEY) === "1";

    for (const a of alerts) {
      if (seenRef.current.has(a.id)) continue;
      seenRef.current.add(a.id);
      if (muted) continue;
      if (a.severity !== "critical" && a.severity !== "high") continue;

      try {
        if (!ctxRef.current) {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (!Ctx) return;
          ctxRef.current = new Ctx();
        }
        const ctx = ctxRef.current;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "square";
        o.frequency.value = a.severity === "critical" ? 880 : 660;
        g.gain.value = 0.04;
        o.connect(g).connect(ctx.destination);
        const t = ctx.currentTime;
        o.start(t);
        g.gain.setValueAtTime(0.04, t);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
        o.stop(t + 0.2);
        if (a.severity === "critical") {
          // second beep
          const o2 = ctx.createOscillator();
          const g2 = ctx.createGain();
          o2.type = "square";
          o2.frequency.value = 988;
          g2.gain.value = 0.04;
          o2.connect(g2).connect(ctx.destination);
          o2.start(t + 0.22);
          g2.gain.setValueAtTime(0.04, t + 0.22);
          g2.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
          o2.stop(t + 0.42);
        }
      } catch {
        /* ignore audio failures */
      }
    }
  }, [alerts, enabled]);
};

export const audioMuted = () => localStorage.getItem(STORAGE_KEY) === "1";
export const setAudioMuted = (v) => localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
